import { getCountries } from '../APICalling/APICalling';

let countriesCache = null;
let countriesRequest = null;
/** O(1) lookup by country code, built once per successful request. */
let codeToCountryMap = null;

const normalizeCode = (code) => String(code || '').trim().toUpperCase();

/**
 * Shares one request across all forms and caches successful responses for this page session.
 * API order, country IDs, dial codes and flag URLs remain authoritative.
 * @returns {Promise<Array>} Array of country objects
 */
export const fetchCountries = () => {
	if (countriesCache !== null) return Promise.resolve(countriesCache);
	if (countriesRequest) return countriesRequest;

	const request = getCountries()
		.then((response) => {
			// APICalling returns the decoded response body, including errors on failure.
			if (response instanceof Error) throw response;
			const countries = response?.data?.data?.countries;
			if (response?.data?.success !== true || !Array.isArray(countries) ||
				countries.some((country) => !country || typeof country.code !== 'string' ||
					!country.code.trim() || country.id == null || typeof country.name !== 'string' ||
					!/^\d+$/.test(String(country.dial_code)) || Number(country.dial_code) <= 0)) {
				throw new Error('Unable to load countries. Please try again.');
			}

			const normalizedCountries = countries.map((country) => ({
				...country,
				code: normalizeCode(country.code),
			}));
			// A request started before clearCountriesCache must not replace a newer cache.
			if (countriesRequest === request) {
				countriesCache = normalizedCountries;
				codeToCountryMap = new Map(normalizedCountries.map((country) => [country.code, country]));
			}
			return normalizedCountries;
		})
		.finally(() => {
			// Failed requests are not cached, so the selector can retry.
			if (countriesRequest === request) countriesRequest = null;
		});

	countriesRequest = request;
	return request;
};

/**
 * Finds a country object by country code (e.g., 'SG', 'US')
 * @param {string} countryCode - The country code to search for
 * @returns {Promise<Object|null>} Country object with {id, code, name, dial_code, image} or null
 */
export const findCountryByCode = async (countryCode) => {
	if (!countryCode) return null;

	const countries = await fetchCountries();
	if (!countries || countries.length === 0) return null;

	return countries === countriesCache
		? findCountryByCodeSync(countryCode)
		: countries.find((country) => country.code === normalizeCode(countryCode)) ?? null;
};

/**
 * Synchronously finds a country object by country code from cached data
 * Note: This will return null if countries haven't been fetched yet
 * @param {string} countryCode - The country code to search for
 * @returns {Object|null} Country object or null
 */
export const findCountryByCodeSync = (countryCode) => {
	if (!countryCode || !codeToCountryMap) return null;
	return codeToCountryMap.get(normalizeCode(countryCode)) ?? null;
};

/**
 * Applies country update to React state - shared logic for async/sync update helpers
 * @param {Object|null} countryObject - Resolved country object or null
 * @param {string} countryCode - Original country code (fallback when object not found)
 * @param {Function} [setSelectedCountry] - State setter for selectedCountry
 * @param {Function} [setFormState] - State setter (functional updater: prev => newState)
 * @param {string} countryIdField - Field name in form state for country_id
 */
const applyCountryUpdate = (countryObject, countryCode, setSelectedCountry, setFormState, countryIdField) => {
	if (countryObject) {
		setSelectedCountry?.(countryObject);
		if (setFormState) {
			setFormState((prev) => ({
				...prev,
				[countryIdField]: countryObject.id ?? null,
			}));
		}
	} else {
		setSelectedCountry?.(countryCode);
	}
};

/**
 * Updates country state and country_id in form state
 * @param {string} countryCode - The country code detected from phone number
 * @param {Function} [setSelectedCountry] - State setter for selectedCountry
 * @param {Function} [setFormState] - State setter (must use functional updater: prev => newState)
 * @param {string} [countryIdField='country_id'] - The field name in form state for country_id
 */
export const updateCountryFromCode = async (
	countryCode,
	setSelectedCountry,
	setFormState,
	countryIdField = 'country_id'
) => {
	if (!countryCode) return;

	const countryObject = await findCountryByCode(countryCode);
	applyCountryUpdate(countryObject, countryCode, setSelectedCountry, setFormState, countryIdField);
};

/**
 * Synchronous version of updateCountryFromCode that uses cached data
 * Note: This will only work if countries have been fetched already
 * @param {string} countryCode - The country code detected from phone number
 * @param {Function} [setSelectedCountry] - State setter for selectedCountry
 * @param {Function} [setFormState] - State setter (must use functional updater: prev => newState)
 * @param {string} [countryIdField='country_id'] - The field name in form state for country_id
 */
export const updateCountryFromCodeSync = (
	countryCode,
	setSelectedCountry,
	setFormState,
	countryIdField = 'country_id'
) => {
	if (!countryCode) return;

	const countryObject = findCountryByCodeSync(countryCode);
	applyCountryUpdate(countryObject, countryCode, setSelectedCountry, setFormState, countryIdField);
};

/**
 * Clears the countries cache (useful for testing or forcing a refresh)
 */
export const clearCountriesCache = () => {
	countriesCache = null;
	countriesRequest = null;
	codeToCountryMap = null;
};

/**
 * Gets the cached countries list (returns null if not loaded yet)
 * @returns {Array|null} Cached countries array or null
 */
export const getCachedCountries = () => {
	return countriesCache;
};

/**
 * Returns the digits after the country calling code exactly as entered.
 * Unlike libphonenumber's nationalNumber, this keeps leading zeros (e.g. '+60045765768' -> '045765768').
 * @param {string} phone - Full phone number with country code
 * @param {Object} [parsed] - Result of parsePhoneNumber(phone)
 * @returns {string} Local digits without the calling code
 */
export const getLocalPhoneDigits = (phone, parsed) => {
	const digits = String(phone || '').replace(/\D/g, '');
	const callingCode = String(parsed?.countryCallingCode || '');
	return callingCode && digits.startsWith(callingCode)
		? digits.slice(callingCode.length)
		: digits;
};
